import getPageName from "../getPageName";
import { DATES } from "./dates";
import getDataProgram from "./getDataProgram";

class ProgramDropdownManager {
  static EXCLUDED_PROGRAMS = new Set([
    "ai-profession",
    "ai-start",
    "ai-start-fb",
    "ai-start-tg",
    "promptengineertg",
    "marketplacetg",
    "new-marketplace",
    "new-graphicdesign",
    "new-developer1c",
    "ai-course",
    "promptengineering",
    "data-analytics-online-a",
    "data-analytics-online",
    "business-prompt",
    "graph",
    "trade-with-china",
    "ai-system",
    "1c-programmer",
    "ai-design",
    "ai-marketplace",
    "psychology-for-beginners",
    "data-analytics-1",
    "data-analytics-2",
    "accountant_entrum",
    "ai-design-1"
  ]);

  static DATES_PROGRAMS = DATES;

  static SELECTOR = {
    // [data-select] корневые блоки кастомного селекта
    regionSelect: "form-region",
    programSelect: "form-program",
    dateSelect: "form-date",
    hiddenFields: ".form-program-hidden",
    operatorField: ".form-program-hidden-operator",
    shortnameField: ".form-program-hidden-shortname",
    programGroupIdField: ".form-program-hidden-group-id",
    programIdField: ".form-program-hidden-id",
    programHint: '[data-hint="form-program"]',
    regionNameField: ".form-region-hidden-name",
  };

  static HINT_VISIBLE_CLASS = "form__field-hint--visible";

  // Регион с этим id всегда показывается последним в списке (бизнес-правило)
  static REGION_ID_TO_END = 90;

  constructor(requestPrograms, regions) {
    this.requestPrograms = requestPrograms;
    this.regions = regions || [];

    // Корневые .custom-select блоки (data-id="form-region" / "form-program" / "form-date")
    this.regionSelectBlock = document.querySelector(
      `[data-select][data-id="${this.constructor.SELECTOR.regionSelect}"]`,
    );
    this.programSelectBlock = document.querySelector(
      `[data-select][data-id="${this.constructor.SELECTOR.programSelect}"]`,
    );
    this.dateSelectBlock = document.querySelector(
      `[data-select][data-id="${this.constructor.SELECTOR.dateSelect}"]`,
    );

    this.hiddenFields = document.querySelectorAll(
      this.constructor.SELECTOR.hiddenFields,
    );

    // Доп. скрытые поля, в которые пишутся operator/shortname/program_group_id/program_id.
    // Присутствуют в обеих ветках разметки (со страницей-программой и без неё).
    this.operatorField = document.querySelector(
      this.constructor.SELECTOR.operatorField,
    );
    this.shortnameField = document.querySelector(
      this.constructor.SELECTOR.shortnameField,
    );
    this.programGroupIdField = document.querySelector(
      this.constructor.SELECTOR.programGroupIdField,
    );
    this.programIdField = document.querySelector(
      this.constructor.SELECTOR.programIdField,
    );

    // Подсказка "Выберите регион..." лежит рядом с блоком селекта программы,
    // а не внутри него (иначе из-за маленьких отступов между полями она
    // визуально слипается с соседним полем)
    this.programHint = document.querySelector(
      this.constructor.SELECTOR.programHint,
    );

    // Селект региона хранит в value id (нужен для внутренней фильтрации
    // программ/дат по региону), а под капотом отправляется как "RegionId".
    // Название региона для читаемого поля "Region" пишем сюда отдельно.
    this.regionNameField = document.querySelector(
      this.constructor.SELECTOR.regionNameField,
    );

    this.shortUrl = getPageName();
    this.hasHiddenFields = this.hiddenFields.length > 0;

    // Инстанс CustomSelect создаётся в js/index.js и кладёт себя в
    // window.customSelectInstance. Дожидаемся его в initialize(), а не здесь,
    // чтобы не зависеть от порядка вызова initForm()/CustomSelect в index.js.
    this.customSelect = null;
  }

  /**
   * Дожидается появления window.customSelectInstance (создаётся в
   * js/index.js при инициализации CustomSelect). Защищает от гонки, если
   * initDropDownsPrograms() случайно вызовут раньше, чем CustomSelect успеет
   * проинициализироваться.
   *
   * @param {number} timeout - максимальное время ожидания в мс
   * @param {number} interval - интервал между проверками в мс
   * @returns {Promise<CustomSelect|null>}
   */
  static waitForCustomSelectInstance(timeout = 3000, interval = 50) {
    if (window.customSelectInstance) {
      return Promise.resolve(window.customSelectInstance);
    }

    return new Promise((resolve) => {
      const startedAt = Date.now();

      const check = () => {
        if (window.customSelectInstance) {
          resolve(window.customSelectInstance);
          return;
        }
        if (Date.now() - startedAt >= timeout) {
          resolve(null);
          return;
        }
        setTimeout(check, interval);
      };

      check();
    });
  }

  async initialize() {
    this.customSelect = await this.constructor.waitForCustomSelectInstance();

    if (!this.customSelect) {
      console.error(
        "CustomSelect instance не найден (window.customSelectInstance). Убедитесь, что custom-select.js инициализирован.",
      );
      return;
    }

    this.setupFields();
    this.setupEventHandlers();
  }

  setupFields() {
    this.setupRegionField();

    if (this.hasHiddenFields) {
      this.setupHiddenFields();
    } else {
      // На главной программа остаётся неактивной, пока не выбран регион
      this.disableProgramField();
    }
  }

  setupEventHandlers() {
    this.setupRegionEventHandlers();
  }

  /* ==================== Регион ==================== */

  getPageProgram() {
    return this.requestPrograms.find(
      (program) => program.shortname === this.shortUrl,
    );
  }

  /**
   * Заполняет селект региона. На странице конкретной программы список
   * ограничен только теми регионами, в которых эта программа реально
   * доступна; на главной — доступны все регионы.
   */
  setupRegionField() {
    if (!this.regionSelectBlock || !this.customSelect) return;

    const orderedRegions = this.moveRegionToEnd(
      this.regions,
      this.constructor.REGION_ID_TO_END,
    );

    const availableRegions = this.hasHiddenFields
      ? this.filterRegionsForPageProgram(orderedRegions)
      : orderedRegions;

    const items = availableRegions.map((region) => ({
      text: region.name,
      value: region.id,
    }));

    this.customSelect.updateItems(this.regionSelectBlock, items);
  }

  /* Регион с указанным id всегда должен быть последним в списке (бизнес-правило) */
  moveRegionToEnd(regions, regionId) {
    const data = [...regions];
    const targetRegionIndex = data.findIndex((region) => region.id == regionId);

    if (targetRegionIndex !== -1) {
      const [targetRegion] = data.splice(targetRegionIndex, 1);
      data.push(targetRegion);
    }

    return data;
  }

  filterRegionsForPageProgram(regions) {
    const pageProgram = this.getPageProgram();

    if (!pageProgram) return regions;

    return regions.filter((region) =>
      pageProgram.regions?.some(
        (programRegion) => programRegion.region_id == region.id,
      ),
    );
  }

  setupRegionEventHandlers() {
    if (!this.regionSelectBlock) return;

    const nativeSelect = this.regionSelectBlock.querySelector("select");
    if (!nativeSelect) return;

    nativeSelect.addEventListener(
      "change",
      this.hasHiddenFields
        ? this.handleProgramPageRegionChange.bind(this)
        : this.handleHomeRegionChange.bind(this),
    );
  }

  /* ==================== Главная страница (без фиксированной программы) ==================== */

  handleHomeRegionChange(event) {
    const regionId = event.target.value;

    this.updateRegionNameField(event.target);

    /* Смена региона сбрасывает и программу, и дату */
    this.resetProgramField();
    this.resetDateField();

    if (!regionId) {
      /* Регион не выбран — поле "Программа" снова неактивно */
      this.disableProgramField();
      return;
    }

    this.enableProgramField();
    this.renderProgramOptions(regionId);
  }

  handleProgramChange(event) {
    const regionId = this.getSelectedRegionId();
    const selectedOption = event.target.options[event.target.selectedIndex];
    const data = getDataProgram(this.requestPrograms, selectedOption, regionId);
    if (!data) return;

    this.setProgramHiddenFields(data);
    this.updateDateOptions(data.shortname);
  }

  getSelectedRegionId() {
    return this.regionSelectBlock?.querySelector("select")?.value || null;
  }

  /**
   * Пишет читаемое название выбранного региона в отдельное hidden-поле
   * "Region" — сам селект хранит в value id региона (нужен для фильтрации
   * программ/дат), а не название.
   */
  updateRegionNameField(selectElement) {
    if (!this.regionNameField) return;
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    this.regionNameField.value = selectedOption?.text || "";
  }

  resetProgramField() {
    if (!this.programSelectBlock || !this.customSelect) return;
    this.customSelect.updateItems(this.programSelectBlock, []);
    this.bindProgramChangeListener();
  }

  resetDateField() {
    if (!this.dateSelectBlock || !this.customSelect) return;
    this.customSelect.updateItems(this.dateSelectBlock, []);
    this.customSelect.setDisabled(this.dateSelectBlock, true);
  }

  /* Поле "Программа" неактивно, пока не выбран регион */
  disableProgramField() {
    if (!this.programSelectBlock || !this.customSelect) return;
    this.customSelect.setDisabled(this.programSelectBlock, true);
    this.showProgramRegionHint();
  }

  enableProgramField() {
    if (!this.programSelectBlock || !this.customSelect) return;
    this.customSelect.setDisabled(this.programSelectBlock, false);
    this.hideProgramRegionHint();
  }

  showProgramRegionHint() {
    this.programHint?.classList.add(
      this.constructor.HINT_VISIBLE_CLASS,
    );
  }

  hideProgramRegionHint() {
    this.programHint?.classList.remove(
      this.constructor.HINT_VISIBLE_CLASS,
    );
  }

  /**
   * Переподписывается на "change" нативного select программы. Нужно вызывать
   * каждый раз после customSelect.updateItems() на этом блоке, так как
   * updateItems пересобирает <select> с нуля и старый обработчик отваливается
   * вместе с удалённым элементом.
   */
  bindProgramChangeListener() {
    if (!this.programSelectBlock) return;
    const nativeSelect = this.programSelectBlock.querySelector("select");
    if (!nativeSelect) return;
    nativeSelect.addEventListener("change", this.handleProgramChange.bind(this));
  }

  /**
   * Записывает operator/shortname/program_group_id/program_id в отдельные
   * именованные hidden-поля формы (form-program-hidden-*), чтобы они попали
   * в FormData при сабмите. Поле .form-program-hidden хранит только name
   * программы (как и раньше).
   */
  setProgramHiddenFields(data) {
    this.hiddenFields.forEach((field) => {
      if (data.name) {
        field.value = data.name;
      }
    });

    if (this.operatorField) {
      this.operatorField.value = data.operator ?? "";
    }
    if (this.shortnameField) {
      this.shortnameField.value = data.shortname ?? "";
    }
    if (this.programGroupIdField) {
      this.programGroupIdField.value = data.program_group_id ?? "";
    }
    if (this.programIdField) {
      this.programIdField.value = data.program_id ?? "";
    }
  }

  /**
   * Пересобирает список опций селекта дат под выбранную программу
   * и разблокирует сам селект.
   */
  updateDateOptions(shortname) {
    if (!this.dateSelectBlock) return;

    const dates = this.constructor.DATES_PROGRAMS[shortname];

    if (!dates) {
      console.error("Не переданы даты для программы!");
      this.customSelect.updateItems(this.dateSelectBlock, []);
      this.customSelect.setDisabled(this.dateSelectBlock, true);
      return;
    }

    const items = dates.map((date) => ({
      text: date.name,
      value: date.date,
    }));

    this.customSelect.updateItems(this.dateSelectBlock, items);
    this.customSelect.setDisabled(this.dateSelectBlock, false);
  }

  filterPrograms(programs) {
    return programs.filter(
      (program) => !this.constructor.EXCLUDED_PROGRAMS.has(program.shortname),
    );
  }

  isProgramAvailableInRegion(program, regionId) {
    return !!program.regions?.some((region) => region.region_id == regionId);
  }

  separateProgramsByRedirect(programs) {
    return programs.reduce(
      (acc, program) => {
        const hasRedirect = program.regions?.some((region) =>
          region.fields?.some((field) => field.field === "rvr_link"),
        );

        if (hasRedirect) {
          acc.withRedirect.push(program);
        } else {
          acc.withoutRedirect.push(program);
        }

        return acc;
      },
      { withRedirect: [], withoutRedirect: [] },
    );
  }

  /**
   * Строит items для customSelect.updateItems() из списка программ,
   * доступных в выбранном регионе, с программами withRedirect впереди,
   * withoutRedirect — после них.
   */
  renderProgramOptions(regionId) {
    if (!this.programSelectBlock || !this.customSelect) return;

    const availablePrograms = this.filterPrograms(this.requestPrograms).filter(
      (program) => this.isProgramAvailableInRegion(program, regionId),
    );
    const { withRedirect, withoutRedirect } =
      this.separateProgramsByRedirect(availablePrograms);

    const orderedPrograms = [...withRedirect, ...withoutRedirect];

    const items = orderedPrograms.map((program) => ({
      text: program.name,
      value: program.name,
      shortname: program.shortname,
    }));

    this.customSelect.updateItems(this.programSelectBlock, items);

    // updateItems пересобирает <option> через createNativeSelectMarkup, который
    // не знает про "shortname" — донавешиваем его на нативные <option>, так как
    // getDataProgram() ищет программу именно по этому атрибуту.
    this.applyShortnameToNativeOptions(orderedPrograms);
    this.bindProgramChangeListener();
  }

  /**
   * createNativeSelectMarkup создаёт <option> только с value/text, поэтому
   * после updateItems донавешиваем shortname на каждый <option> в том же
   * порядке, в котором программы были переданы в items.
   */
  applyShortnameToNativeOptions(orderedPrograms) {
    if (!this.programSelectBlock) return;
    const select = this.programSelectBlock.querySelector("select");
    if (!select) return;

    // options[0] — пустой placeholder из createNativeSelectMarkup, поэтому +1
    orderedPrograms.forEach((program, index) => {
      const option = select.options[index + 1];
      if (option) {
        option.setAttribute("shortname", program.shortname);
      }
    });
  }

  /* ==================== Страница программы (программа зафиксирована) ==================== */

  setupHiddenFields() {
    // shortname программы для страницы зафиксирован URL'ом, поэтому
    // достаточно получить data один раз через первый hidden-field
    const referenceField = this.hiddenFields[0];
    if (referenceField) {
      this.setupHiddenField(referenceField);
    }

    if (this.constructor.DATES_PROGRAMS[this.shortUrl]) {
      this.updateDateOptions(this.shortUrl);
    } else {
      console.error("Не переданы даты для программы!");
    }
  }

  setupHiddenField(field) {
    field.setAttribute("shortname", this.shortUrl);

    // Регион ещё не выбран — operator/program_id появятся после этого,
    // а name/shortname/program_group_id не зависят от региона
    const data = getDataProgram(this.requestPrograms, field, null);

    if (!data) return;

    this.setProgramHiddenFields(data);
  }

  handleProgramPageRegionChange(event) {
    const regionId = event.target.value;

    this.updateRegionNameField(event.target);

    const referenceField = this.hiddenFields[0];

    if (!referenceField) return;

    const data = getDataProgram(this.requestPrograms, referenceField, regionId);

    if (!data) return;

    this.setProgramHiddenFields(data);
  }
}

async function initDropDownsPrograms(requestPrograms, regions) {
  const manager = new ProgramDropdownManager(requestPrograms, regions);
  await manager.initialize();
  return manager;
}

export default initDropDownsPrograms;
